import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, ChevronRight, Leaf, Check, AlertTriangle, Wind, Thermometer, Settings } from 'lucide-react';
import Footer from '../../components/Footer';
import { useCanonical } from '../../hooks/useCanonical';

const EGROffPage = () => {
  useCanonical('https://ecuflashservice.com/services/egr-off');
  
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>EGR Delete Service | EGR Valve Removal & Disable | From $60 | Fast Delivery</title>
        <meta name="description" content="Professional EGR delete & removal service. Stop carbon buildup, improve fuel economy, prevent intake clogging. BMW, Mercedes, VW, Audi, Ford diesel. 24h turnaround." />
        <meta name="keywords" content="EGR delete, EGR removal, EGR off, EGR valve delete, exhaust gas recirculation disable, carbon buildup fix" />
        <link rel="canonical" href="https://ecuflashservice.com/services/egr-off" />
        
        {/* Service Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "EGR Delete Service",
            "description": "Professional Exhaust Gas Recirculation (EGR) removal and ECU remapping service. Eliminate carbon buildup, restore engine efficiency, and prevent intake manifold clogging.",
            "image": "https://ecuflashservice.com/favicon-512.png",
            "brand": {
              "@type": "Brand",
              "name": "ECU Flash Service"
            },
            "offers": {
              "@type": "Offer",
              "url": "https://ecuflashservice.com/services/egr-off",
              "price": "60",
              "priceCurrency": "USD",
              "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "Organization",
                "name": "ECU Flash Service",
                "url": "https://ecuflashservice.com"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "bestRating": "5",
              "worstRating": "1",
              "reviewCount": "67"
            }
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <div className="flex items-center space-x-6">
              <Link to="/services/dpf-off" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">DPF OFF</Link>
              <Link to="/services/adblue-off" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">AdBlue OFF</Link>
              <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
                <span>Get Started</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-green-500/30 mb-6">
            <Leaf className="w-4 h-4 text-green-400" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-green-400">Engine Health</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">EGR Delete</span>{' '}
            <span className="text-[#00F0FF]">& Removal Service</span>
          </h1>
          <p className="text-[#888] text-lg max-w-2xl mx-auto mb-8">
            Professional EGR valve software delete. Stop carbon buildup, improve fuel economy & restore engine efficiency. All diesel vehicles supported.
          </p>
          <Link 
            to="/" 
            className="inline-block btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
          >
            <span>Order EGR Delete →</span>
          </Link>
        </div>
      </section>

      {/* What is EGR */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white mb-6">What is EGR Delete?</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                The Exhaust Gas Recirculation (EGR) system redirects a portion of exhaust gases back into the engine&apos;s intake manifold to reduce nitrogen oxide (NOx) emissions. While environmentally beneficial, this system introduces hot, dirty exhaust gases into your engine.
              </p>
              <p className="text-[#888] leading-relaxed mb-4">
                EGR delete is an ECU software modification that disables the EGR valve operation and related monitoring systems. This prevents exhaust gases from recirculating into the engine, keeping the intake system clean and improving combustion efficiency.
              </p>
              <p className="text-[#888] leading-relaxed">
                The modification includes disabling EGR valve control, EGR cooler monitoring, and all related diagnostic trouble codes to ensure smooth operation.
              </p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 p-8">
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-6">What We Disable</h3>
              <ul className="space-y-4">
                {[
                  'EGR valve actuation',
                  'EGR position monitoring',
                  'EGR cooler bypass valve',
                  'EGR temperature sensors',
                  'Related DTC error codes'
                ].map((item, i) => (
                  <li key={i} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-green-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                    <span className="text-[#888]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-4">The EGR Problem</h2>
          <p className="text-[#888] text-center mb-12 max-w-2xl mx-auto">Why many diesel owners choose to delete the EGR system</p>
          
          <div className="bg-[#1A1A1A] border border-white/5 p-8 mb-8">
            <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">Carbon Buildup - The Silent Engine Killer</h3>
            <p className="text-[#888] mb-4">
              When hot, sooty exhaust gases mix with oil vapors from the crankcase ventilation system, they create a sticky, tar-like substance that coats the intake manifold, intake valves, and EGR passages. Over time, this buildup:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Restricts airflow to the engine',
                'Reduces combustion efficiency',
                'Causes rough idle and misfires',
                'Decreases power and throttle response',
                'Increases fuel consumption',
                'Can cause complete EGR valve failure'
              ].map((item, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-[#FF003C] rounded-full"></span>
                  <span className="text-[#888]">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Settings, title: 'EGR Valve Failure', desc: 'Carbon buildup causes the valve to stick open or closed, triggering limp mode' },
              { icon: Thermometer, title: 'Cooler Cracks', desc: 'EGR coolers can crack, leaking coolant into the exhaust and causing overheating' },
              { icon: Wind, title: 'Intake Clogging', desc: 'Severe buildup can reduce intake port size by 50% or more' },
            ].map((item, i) => (
              <div key={i} className="bg-[#1A1A1A] border border-white/5 p-6 hover:border-[#00F0FF]/30 transition-all">
                <item.icon className="w-8 h-8 text-[#00F0FF] mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[#666] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white text-center mb-12">Benefits of EGR Delete</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Cleaner Intake System', desc: 'No more soot and carbon entering your intake manifold. Fresh air only means cleaner combustion.' },
              { title: 'Lower Intake Temps', desc: 'Removing hot exhaust gases reduces intake temperatures, increasing air density and power.' },
              { title: 'Better Throttle Response', desc: 'Cleaner intake paths mean better airflow and more responsive acceleration.' },
              { title: 'Improved Fuel Economy', desc: 'More efficient combustion with fresh air leads to better fuel consumption.' },
              { title: 'Extended Engine Life', desc: 'Eliminating carbon buildup reduces wear on valves, pistons, and turbo.' },
              { title: 'Reduced Maintenance', desc: 'No more EGR valve cleaning, cooler replacements, or intake walnut blasting.' },
            ].map((item, i) => (
              <div key={i} className="bg-[#0A0A0A] border border-white/10 p-6 hover:border-green-500/30 transition-all">
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[#666] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Combine with DPF */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="relative overflow-hidden border border-green-500/20 p-8">
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/5 rounded-full blur-3xl" />
            <Zap className="w-10 h-10 text-green-400 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">Best Results: Combine with DPF Delete</h2>
            <p className="text-[#888] mb-6 max-w-2xl mx-auto relative z-10">
              For maximum performance and reliability gains, many customers choose to delete both EGR and DPF systems together. This combination provides the cleanest intake, lowest exhaust back pressure, and greatest efficiency improvements.
            </p>
            <Link 
              to="/services/dpf-off" 
              className="inline-block btn-skew bg-green-500 text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-green-400 transition-all"
            >
              <span>Learn About DPF Delete →</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-[#FF003C]/10 border border-[#FF003C]/30 p-8">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="w-8 h-8 text-[#FF003C] flex-shrink-0" />
              <div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-[#FF003C] mb-3">Legal Notice</h3>
                <p className="text-[#FF003C]/80 mb-4">
                  EGR delete is intended <strong>ONLY for off-road, racing, and competition vehicles</strong> in jurisdictions where such modifications are permitted. Tampering with emission control devices on road-registered vehicles may violate environmental regulations.
                </p>
                <p className="text-[#FF003C]/60 text-sm">
                  We recommend consulting local regulations before proceeding with any emission system modifications.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-30" />
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4">Ready for a Cleaner Engine?</h2>
          <p className="text-[#888] mb-8">Upload your ECU file and get your EGR delete solution within 20-60 minutes</p>
          <Link 
            to="/" 
            className="inline-block btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
          >
            <span>Start Now →</span>
          </Link>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default EGROffPage;
